import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaCoins, 
  FaUser, 
  FaCalendarAlt, 
  FaCheckCircle, 
  FaTimes, 
  FaClock, 
  FaHourglassHalf,
  FaEdit,
  FaEye,
  FaFilter,
  FaPhone,
  FaEnvelope
} from 'react-icons/fa';
import './RedemptionManagement.css';

const RedemptionManagement = () => {
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRedemption, setSelectedRedemption] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [stats, setStats] = useState({
    byStatus: [],
    totalRedemptions: 0,
    totalCoinsRequested: 0
  });
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    status: '',
    adminNotes: '',
    rejectionReason: ''
  });

  useEffect(() => {
    fetchRedemptions();
    fetchStats();
  }, [statusFilter, pagination.currentPage]);

  const fetchRedemptions = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get('https://api.swasthmitra.com/api/redemption/admin/all', {
        params: {
          status: statusFilter,
          page,
          limit: 10
        }
      });
      
      if (response.data.success) {
        setRedemptions(response.data.redemptions);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching redemptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('https://api.swasthmitra.com/api/redemption/admin/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleStatusUpdate = async (redemptionId, newStatus, notes = '', rejectionReason = '') => {
    try {
      setProcessing(true);
      const response = await axios.put(`https://api.swasthmitra.com/api/redemption/admin/update/${redemptionId}`, {
        status: newStatus,
        adminNotes: notes,
        rejectionReason: rejectionReason
        // Don't send processedBy - let backend handle it or get actual admin ID from context
      });

      if (response.data.success) {
        // Refresh the list
        fetchRedemptions(pagination.currentPage);
        fetchStats();
        setShowModal(false);
        setSelectedRedemption(null);
        
        // Show success message
        alert(`Redemption ${newStatus} successfully!`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update redemption status');
    } finally {
      setProcessing(false);
    }
  };

  const openModal = (redemption) => {
    setSelectedRedemption(redemption);
    setFormData({
      status: redemption.status,
      adminNotes: redemption.adminNotes || '',
      rejectionReason: redemption.rejectionReason || ''
    });
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedRedemption) {
      handleStatusUpdate(
        selectedRedemption._id, 
        formData.status, 
        formData.adminNotes, 
        formData.rejectionReason
      );
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <FaCheckCircle className="status-icon approved" />;
      case 'pending':
        return <FaClock className="status-icon pending" />;
      case 'cancelled':
        return <FaTimes className="status-icon cancelled" />;
      case 'delay':
        return <FaHourglassHalf className="status-icon delay" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    return `status-badge ${status}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatsByStatus = (status) => {
    const stat = stats.byStatus.find(s => s._id === status);
    return stat ? stat.count : 0;
  };

  if (loading && redemptions.length === 0) {
    return (
      <div className="redemption-management">
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading redemption requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="redemption-management">
      {/* Header */}
      <div className="management-header">
        <h2>
          <FaCoins className="header-icon" />
          Coin Redemption Management
        </h2>
        <p>Manage and process employee coin redemption requests</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <FaCoins />
          </div>
          <div className="stat-content">
            <h3>{stats.totalRedemptions}</h3>
            <p>Total Requests</p>
          </div>
        </div>
        
        <div className="stat-card pending">
          <div className="stat-icon">
            <FaClock />
          </div>
          <div className="stat-content">
            <h3>{getStatsByStatus('pending')}</h3>
            <p>Pending</p>
          </div>
        </div>
        
        <div className="stat-card approved">
          <div className="stat-icon">
            <FaCheckCircle />
          </div>
          <div className="stat-content">
            <h3>{getStatsByStatus('approved')}</h3>
            <p>Approved</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="statusFilter">
            <FaFilter className="filter-icon" />
            Filter by Status:
          </label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="cancelled">Cancelled</option>
            <option value="delay">Delayed</option>
          </select>
        </div>
      </div>

      {/* Redemptions Table */}
      <div className="redemptions-table-container">
        <table className="redemptions-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Contact</th>
              <th>Coins Requested</th>
              <th>Total Earned</th>
              <th>Available Balance</th>
              <th>Request Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {redemptions.map((redemption) => (
              <tr key={redemption._id}>
                <td>
                  <div className="employee-info">
                    <div className="employee-details">
                      <span className="employee-name">{redemption.userId?.name}</span>
                      <span className="employee-role">{redemption.userId?.role}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="contact-info">
                    <div className="contact-item">
                      <FaEnvelope className="contact-icon" />
                      <span>{redemption.userId?.email}</span>
                    </div>
                    <div className="contact-item">
                      <FaPhone className="contact-icon" />
                      <span>{redemption.userId?.MobileNumber}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="coins-info">
                    <FaCoins className="coin-icon" />
                    <span className="coin-amount">{redemption.coinsRequested}</span>
                  </div>
                </td>
                <td>
                  <div className="balance-info">
                    {redemption.userCoinData && !redemption.userCoinData.error ? (
                      <div className="total-earned">
                        <FaCoins className="coin-icon" style={{color: '#28a745'}} />
                        <span className="earned-amount">{redemption.userCoinData.totalEarned}</span>
                      </div>
                    ) : (
                      <span className="error-text">Error loading</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="balance-info">
                    {redemption.userCoinData && !redemption.userCoinData.error ? (
                      <div className="balance-details">
                        <div className="balance-item">
                          <span className="available-balance">{redemption.userCoinData.availableBalance}</span>
                        </div>
                        <div className="balance-item small">
                          <span>Redeemed: {redemption.userCoinData.totalRedeemed}</span>
                        </div>
                      </div>
                    ) : (
                      <span className="error-text">Error loading balance</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="date-info">
                    <FaCalendarAlt className="date-icon" />
                    <span>{formatDate(redemption.createdAt)}</span>
                  </div>
                </td>
                <td>
                  <div className="status-cell">
                    {getStatusIcon(redemption.status)}
                    <span className={getStatusBadge(redemption.status)}>
                      {redemption.status.charAt(0).toUpperCase() + redemption.status.slice(1)}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => openModal(redemption)}
                      className="action-btn edit-btn"
                      title="Edit Status"
                    >
                      <FaEdit />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {redemptions.length === 0 && (
          <div className="no-data">
            <FaCoins className="no-data-icon" />
            <h3>No redemption requests found</h3>
            <p>There are no redemption requests matching your current filter.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => fetchRedemptions(pagination.currentPage - 1)}
            disabled={!pagination.hasPrevPage}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <span className="pagination-info">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          
          <button
            onClick={() => fetchRedemptions(pagination.currentPage + 1)}
            disabled={!pagination.hasNextPage}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Modal for editing redemption */}
      {showModal && selectedRedemption && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Update Redemption Request</h3>
              <button
                onClick={() => setShowModal(false)}
                className="close-btn"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="redemption-summary">
                <h4>Request Details</h4>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="label">Employee:</span>
                    <span className="value">{selectedRedemption.userId?.name}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Coins Requested:</span>
                    <span className="value">{selectedRedemption.coinsRequested}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Request Date:</span>
                    <span className="value">{formatDate(selectedRedemption.createdAt)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Current Status:</span>
                    <span className={`value ${getStatusBadge(selectedRedemption.status)}`}>
                      {selectedRedemption.status.charAt(0).toUpperCase() + selectedRedemption.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="update-form">
                <div className="form-group">
                  <label htmlFor="status">Update Status:</label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="form-select"
                    required
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="delay">Delayed</option>
                  </select>
                </div>
                
                {(formData.status === 'cancelled' || formData.status === 'delay') && (
                  <div className="form-group">
                    <label htmlFor="rejectionReason">Reason:</label>
                    <textarea
                      id="rejectionReason"
                      value={formData.rejectionReason}
                      onChange={(e) => setFormData({...formData, rejectionReason: e.target.value})}
                      className="form-textarea"
                      placeholder="Please provide a reason for cancellation or delay..."
                      rows="3"
                      required
                    />
                  </div>
                )}
                
                <div className="form-group">
                  <label htmlFor="adminNotes">Admin Notes (Optional):</label>
                  <textarea
                    id="adminNotes"
                    value={formData.adminNotes}
                    onChange={(e) => setFormData({...formData, adminNotes: e.target.value})}
                    className="form-textarea"
                    placeholder="Add any additional notes..."
                    rows="3"
                  />
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn btn-secondary"
                    disabled={processing}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={processing}
                  >
                    {processing ? 'Updating...' : 'Update Status'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedemptionManagement;